import { FC } from 'react'
import { Modal, Form, Input, Button, Alert, Typography } from 'antd'
import { UserOutlined, LockOutlined, CloseOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../hooks/useAuth'
import { GoogleLoginButton } from './GoogleLoginButton'
import './auth-modal.css'

const { Title, Text } = Typography

interface LoginModalProps {
  visible: boolean
  onClose: () => void
  onSwitchToRegister: () => void
  onSwitchToForgotPassword: () => void
}

export const LoginModal: FC<LoginModalProps> = ({
  visible,
  onClose,
  onSwitchToRegister,
  onSwitchToForgotPassword
}) => {
  const { t } = useTranslation(['common', 'auth'])
  const { signIn, loading, error, clearError } = useAuth()
  const [form] = Form.useForm()

  const handleSubmit = async (values: { email: string; password: string }) => {
    const { success } = await signIn(values.email, values.password)
    if (success) {
      form.resetFields()
      onClose()
    }
  }

  const handleCancel = () => {
    clearError()
    form.resetFields()
    onClose()
  }

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={400}
      centered
      closable={true}
      maskClosable={true}
      closeIcon={<CloseOutlined className="auth-modal__close-icon" />}
    >
      <div className="auth-modal__header">
        <Title level={2} className="auth-modal__title">
          {t('auth:login.title')}
        </Title>
        <Text type="secondary" className="auth-modal__subtitle">
          {t('auth:login.subtitle')}
        </Text>
      </div>

      {error && (
        <Alert
          message={error}
          type="error"
          showIcon
          className="auth-modal__alert"
          closable
          onClose={clearError}
        />
      )}

      <Form
        form={form}
        onFinish={handleSubmit}
        layout="vertical"
        size="large"
      >
        <Form.Item
          name="email"
          label={t('auth:form.email')}
          rules={[
            { required: true, message: t('auth:validation.emailRequired') },
            { type: 'email', message: t('auth:validation.emailInvalid') }
          ]}
        >
          <Input
            prefix={<UserOutlined className="auth-modal__input-icon" />}
            placeholder={t('auth:placeholder.email')}
            autoComplete="email"
          />
        </Form.Item>

        <Form.Item
          name="password"
          label={t('auth:form.password')}
          rules={[
            { required: true, message: t('auth:validation.passwordRequired') },
            { min: 6, message: t('auth:validation.passwordMinLength') }
          ]}
        >
          <Input.Password
            prefix={<LockOutlined className="auth-modal__input-icon" />}
            placeholder={t('auth:placeholder.password')}
            autoComplete="current-password"
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            className="auth-modal__submit"
          >
            {t('auth:login.submit')}
          </Button>
        </Form.Item>

        <div className="auth-modal__link-wrap auth-modal__link-wrap--compact">
          <Button
            type="link"
            onClick={onSwitchToForgotPassword}
            className="auth-modal__link"
          >
            {t('auth:button.forgotPassword')}
          </Button>
        </div>

        <div className="auth-modal__link-wrap auth-modal__link-wrap--spaced">
          <Text type="secondary" className="auth-modal__text">
            {t('auth:login.noAccount')}{' '}
            <Button
              type="link"
              onClick={onSwitchToRegister}
              className="auth-modal__link auth-modal__link--strong"
            >
              {t('auth:login.registerNow')}
            </Button>
          </Text>
        </div>

        {/* Google 登录按钮 */}
        <GoogleLoginButton onSuccess={onClose} />
      </Form>
    </Modal>
  )
}
